/**
 * Revenue Chart Component
 *
 * Plots one point per calendar month of released payments. The caller owns the
 * bucketing and the totals; this component only draws what it is handed, and
 * renders the change row only when the caller passes one.
 */

import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { FloatPanel } from "@/features/studio/theme";
import { useThemeColor } from "@/hooks/use-theme-color";

interface RevenueData {
	month: string;
	revenue: number;
}

interface RevenueChange {
	/** Pre-formatted percentage, sign included. */
	value: string;
	/** What the change is measured against, e.g. "since last month". */
	label: string;
	isPositive: boolean;
}

interface RevenueChartProps {
	title: string;
	data: RevenueData[];
	totalRevenue: string;
	totalLabel: string;
	change?: RevenueChange;
}

export function RevenueChart({
	title,
	data,
	totalRevenue,
	totalLabel,
	change,
}: RevenueChartProps) {
	// Recharts takes colors as SVG attributes; see useThemeColor.
	const gridColor = useThemeColor("--color-dashboard-border", "#E2E8F0");
	const axisColor = useThemeColor("--color-dashboard-muted", "#64748B");
	const accentColor = useThemeColor("--color-dashboard-accent", "#EA580C");
	return (
		<FloatPanel
			elevation={1}
			padding={20}
			style={{ background: "var(--color-dashboard-paper)" }}
		>
			<div
				style={{
					borderBottom: "1px solid var(--color-dashboard-border-soft)",
					paddingBottom: 14,
					marginBottom: 16,
				}}
			>
				<h2
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 600,
						fontSize: 16,
						color: "var(--color-dashboard-text)",
					}}
				>
					{title}
				</h2>
			</div>

			<div className="mb-3">
				<ResponsiveContainer width="100%" height={200}>
					<LineChart data={data}>
						<CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
						<XAxis
							dataKey="month"
							stroke={axisColor}
							fontSize={11}
							tickLine={false}
							style={{ fontFamily: "var(--font-mono)" }}
						/>
						<YAxis
							stroke={axisColor}
							fontSize={11}
							tickLine={false}
							style={{ fontFamily: "var(--font-mono)" }}
							tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: "var(--color-dashboard-paper)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: "7px",
								fontFamily: "var(--font-body)",
								fontSize: 12,
							}}
							formatter={(value) => {
								const numeric =
									typeof value === "number"
										? value
										: typeof value === "string"
											? Number(value)
											: Number.NaN;
								return Number.isFinite(numeric)
									? `₹${numeric.toLocaleString("en-IN")}`
									: "";
							}}
						/>
						<Line
							type="monotone"
							dataKey="revenue"
							stroke={accentColor}
							strokeWidth={2.5}
							dot={{ fill: accentColor, r: 4 }}
							activeDot={{ r: 6 }}
							name="Revenue"
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>

			<div
				style={{
					display: "flex",
					alignItems: "flex-end",
					justifyContent: "space-between",
					borderTop: "1px solid var(--color-dashboard-border-soft)",
					paddingTop: 14,
					marginTop: 14,
				}}
			>
				<div>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 24,
							fontWeight: 700,
							color: "var(--color-dashboard-text)",
							letterSpacing: "-0.01em",
						}}
					>
						{totalRevenue}
					</div>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 11,
							color: "var(--color-dashboard-muted)",
							marginTop: 2,
						}}
					>
						{totalLabel}
					</p>
				</div>
				{change && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontFamily: "var(--font-body)",
						}}
					>
						<span
							style={{
								fontSize: 13,
								fontWeight: 600,
								color: change.isPositive
									? "var(--color-dashboard-pill-done)"
									: "var(--color-dashboard-pill-risk)",
							}}
						>
							{change.value}
						</span>
						<span
							style={{ fontSize: 11, color: "var(--color-dashboard-muted)" }}
						>
							{change.label}
						</span>
					</div>
				)}
			</div>
		</FloatPanel>
	);
}
